import React, { useState, useEffect } from 'react'
import { getApiUrl, apiRequest } from '../utils/api';

const CampaignForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    influencer_name: '',
    post_link: '',
    team_member: '',
    product: '',
    platform: 'Instagram',
    tier: '',
    amount: '',
    views: '',
    likes: '',
    comments: '',
    reposts: '',
    shares: '',
    influencer_info: null
  });
  const [influencerDetails, setInfluencerDetails] = useState(null);

  const [teamMembers, setTeamMembers] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTeamMembers = () => {
    apiRequest(`/team-members`)
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setTeamMembers(data.team_members);
        }
      })
      .catch(error => console.error('Error fetching team members:', error));
  };

  const fetchProducts = () => {
    apiRequest(`/products`)
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setProducts(data.products);
        }
      })
      .catch(error => console.error('Error fetching products:', error));
  };

  useEffect(() => {
    // Initial fetch
    fetchTeamMembers();
    fetchProducts();

    // Refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchTeamMembers();
        fetchProducts();
      }
    };

    // Periodic refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchTeamMembers();
      fetchProducts();
    }, 30000);

    // Listen for storage events (cross-tab synchronization)
    const handleStorageChange = (e) => {
      if (e.key === 'team_members_updated') {
        fetchTeamMembers();
      }
      if (e.key === 'products_updated') {
        fetchProducts();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Auto-fill tier and influencer details if influencer_info is present in formData
  useEffect(() => {
    if (formData.influencer_info) {
      let info = formData.influencer_info;
      if (typeof info === 'string') {
        try {
          info = JSON.parse(info);
        } catch {}
      }
      setInfluencerDetails(info);
      // Auto-select tier
      let followers = info?.followersCount || info?.followers;
      if (followers) {
        followers = parseInt(followers);
        let tier = '';
        if (followers < 10000) tier = 'Nano';
        else if (followers < 50000) tier = 'Micro';
        else if (followers < 200000) tier = 'Mid';
        else if (followers < 1000000) tier = 'Macro';
        else tier = 'Mega';
        setFormData(prev => ({ ...prev, tier }));
      }
    }
  }, [formData.influencer_info]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${getApiUrl()}/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        const action = result.action === 'updated' ? 'updated' : 'created';
        alert(`Campaign ${action} successfully!`);
        // Reset form
        setFormData({
          date: new Date().toISOString().split('T')[0],
          influencer_name: '',
          post_link: '',
          team_member: '',
          product: '',
          platform: 'Instagram',
          tier: 'Tier 1',
          amount: '',
          views: '',
          likes: '',
          comments: '',
          reposts: '',
          shares: ''
        });
      } else {
        alert('Error creating campaign: ' + result.error);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error creating campaign');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Add Campaign</h2>
        <p className="text-gray-600">Manually add a new influencer marketing campaign</p>
      </div>

      {/* Influencer Account Details */}
      {influencerDetails && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <h4 className="text-md font-semibold text-purple-700 mb-2">Influencer Account Details</h4>
          <div className="text-sm text-gray-700">
            <div><strong>Username:</strong> {influencerDetails.username || influencerDetails.userName || influencerDetails.name}</div>
            <div><strong>Followers:</strong> {influencerDetails.followersCount || influencerDetails.followers}</div>
            <div><strong>Bio:</strong> {influencerDetails.bio || influencerDetails.description}</div>
            <div><strong>Profile Link:</strong> {influencerDetails.url ? <a href={influencerDetails.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Open</a> : 'N/A'}</div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Influencer Name
                </label>
                <input
                  type="text"
                  name="influencer_name"
                  value={formData.influencer_name}
                  onChange={handleChange}
                  required
                  placeholder="Enter influencer name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Post Link
                </label>
                <input
                  type="url"
                  name="post_link"
                  value={formData.post_link}
                  onChange={handleChange}
                  placeholder="https://instagram.com/p/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Member
                </label>
                <select
                  name="team_member"
                  value={formData.team_member}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select team member</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.name}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product
                </label>
                <select
                  name="product"
                  value={formData.product}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select product</option>
                  {products.map(product => (
                    <option key={product.id} value={product.name}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platform
                </label>
                <select
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Instagram">Instagram</option>
                  <option value="TikTok">TikTok</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Twitter">Twitter</option>
                  <option value="LinkedIn">LinkedIn</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tier
                </label>
                <select
                  name="tier"
                  value={formData.tier}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Nano">Nano (1K-10K)</option>
                  <option value="Micro">Micro (10K-50K)</option>
                  <option value="Mid">Mid (50K-200K)</option>
                  <option value="Macro">Macro (200K-1M)</option>
                  <option value="Mega">Mega (1M+)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (₹)
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Views
                </label>
                <input
                  type="number"
                  name="views"
                  value={formData.views}
                  onChange={handleChange}
                  required
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Engagement Metrics */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Engagement Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Likes
                </label>
                <input
                  type="number"
                  name="likes"
                  value={formData.likes}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments
                </label>
                <input
                  type="number"
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reposts
                </label>
                <input
                  type="number"
                  name="reposts"
                  value={formData.reposts}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shares
                </label>
                <input
                  type="number"
                  name="shares"
                  value={formData.shares}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Campaign'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignForm;